// settings-interaction.ts — AURA Control Panel
// Full-screen overlay: datasets, thresholds, permissions, mode-level variables
//
// Persistence: hybrid localStorage + Supabase via api/settings.ts

import { saveSetting, getSetting, loadAllSettings, subscribeToChanges } from '../api/settings';

// ─── Types ──────────────────────────────────────────────────────

interface Field {
  key: string;
  label: string;
  type: 'toggle' | 'select' | 'number' | 'text' | 'range' | 'radio' | 'tags' | 'readout';
  options?: string[];
  default?: any;
  hint?: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

interface Group {
  title: string;
  description?: string;
  fields: Field[];
}

interface ModeTab {
  id: string;
  title: string;
  groups: Group[];
}

interface Section {
  id: string;
  title: string;
  subtitle: string;
  groups?: Group[];
  modeTabs?: ModeTab[];
}

// ─── Section Definitions ────────────────────────────────────────

const SECTIONS: Section[] = [
  // ── 1. Account & Security ─────────────────────────────────────
  {
    id: 'account',
    title: 'Account & Security',
    subtitle: 'Authentication, sessions, and API access',
    groups: [
      {
        title: 'Profile',
        fields: [
          { key: 'account.username', label: 'Username', type: 'text', default: '' },
          { key: 'account.email', label: 'Email', type: 'text', default: '' },
          { key: 'account.password', label: 'Password', type: 'text', default: '••••••••' },
        ]
      },
      {
        title: 'Security',
        fields: [
          { key: 'account.2fa', label: 'Two-Factor Authentication', type: 'toggle', default: false },
          { key: 'account.sessions', label: 'Active Sessions', type: 'readout', default: '1 active' },
          { key: 'account.apiKeys', label: 'API Keys', type: 'readout', default: 'None generated' },
        ]
      }
    ]
  },

  // ── 2. Organization & Billing ─────────────────────────────────
  {
    id: 'organization',
    title: 'Organization & Billing',
    subtitle: 'Team structure, roles, and usage limits',
    groups: [
      {
        title: 'Organization',
        fields: [
          { key: 'org.name', label: 'Organization Name', type: 'text', default: '' },
          { key: 'org.members', label: 'Team Members', type: 'readout', default: '1 member' },
          { key: 'org.plan', label: 'Billing Plan', type: 'readout', default: 'Pro' },
        ]
      },
      {
        title: 'Roles',
        description: 'Viewer · Strategist · Creator · Activator · Steward · Admin',
        fields: [
          { key: 'org.defaultRole', label: 'Default Role for New Members', type: 'select', options: ['Viewer', 'Strategist', 'Creator', 'Activator', 'Steward'], default: 'Viewer' },
        ]
      },
      {
        title: 'Usage Limits',
        fields: [
          { key: 'org.maxDatasets', label: 'Max Datasets', type: 'number', default: 10, min: 1, max: 100 },
          { key: 'org.maxActivations', label: 'Max Activations', type: 'number', default: 50, min: 1, max: 500 },
          { key: 'org.compute', label: 'Compute Allocation', type: 'readout', default: 'Standard tier' },
        ]
      }
    ]
  },

  // ── 3. Dataset Profiles ───────────────────────────────────────
  {
    id: 'datasets',
    title: 'Dataset Profiles',
    subtitle: 'Define the world AURA observes',
    groups: [
      {
        title: 'Active Dataset',
        fields: [
          { key: 'dataset.name', label: 'Dataset Name', type: 'text', default: 'Primary Market' },
          { key: 'dataset.description', label: 'Description', type: 'text', default: '' },
        ]
      },
      {
        title: 'Data Sources',
        fields: [
          { key: 'dataset.sources', label: 'Active Sources', type: 'tags', options: ['Search / SERP', 'Social', 'Commerce', 'Partner / Internal'], default: ['Search / SERP', 'Social'] },
        ]
      },
      {
        title: 'Scope',
        fields: [
          { key: 'dataset.geography', label: 'Geography', type: 'text', default: 'Global' },
          { key: 'dataset.language', label: 'Language', type: 'select', options: ['English', 'Multi-language', 'Spanish', 'French', 'German', 'Mandarin', 'Japanese'], default: 'English' },
          { key: 'dataset.timeHorizon', label: 'Time Horizon', type: 'select', options: ['7 days', '30 days', '90 days', '6 months', '1 year', '2 years'], default: '90 days' },
        ]
      },
      {
        title: 'Taxonomy & Filtering',
        fields: [
          { key: 'dataset.taxonomy', label: 'Category Taxonomy', type: 'text', default: '', hint: 'Comma-separated categories' },
          { key: 'dataset.exclusions', label: 'Exclusions', type: 'text', default: '', hint: 'Brands, noise terms (comma-separated)' },
          { key: 'dataset.cadence', label: 'Update Cadence', type: 'select', options: ['Real-time', 'Hourly', 'Daily', 'Weekly'], default: 'Daily' },
          { key: 'dataset.sensitivity', label: 'Sensitivity Level', type: 'radio', options: ['Exploratory', 'Balanced', 'Conservative'], default: 'Balanced' },
        ]
      }
    ]
  },

  // ── 4. Mode Controls ──────────────────────────────────────────
  {
    id: 'modes',
    title: 'Mode Controls',
    subtitle: 'Per-mode variables that govern system behavior',
    modeTabs: [
      // ── Sense ───────────────────────────────────────────────
      {
        id: 'sense',
        title: 'Sense',
        groups: [
          {
            title: 'Signal Detection',
            description: 'How AURA identifies emerging signals from noise',
            fields: [
              { key: 'sense.sensitivity', label: 'Detection Sensitivity', type: 'range', default: 5, min: 1, max: 10, step: 1 },
              { key: 'sense.volumeThreshold', label: 'Minimum Volume Threshold', type: 'number', default: 100, min: 10, max: 10000, unit: 'mentions' },
              { key: 'sense.categoryWeighting', label: 'Category Weighting', type: 'select', options: ['Equal', 'Momentum-biased', 'Decay-biased', 'Custom'], default: 'Equal' },
            ]
          },
          {
            title: 'Source & Refresh',
            fields: [
              { key: 'sense.sourcePriority', label: 'Source Priority', type: 'select', options: ['Equal weight', 'Search-first', 'Social-first', 'Commerce-first'], default: 'Equal weight' },
              { key: 'sense.noiseGate', label: 'Noise Gate', type: 'radio', options: ['Strict', 'Balanced', 'Permissive'], default: 'Balanced' },
              { key: 'sense.scanCadence', label: 'Scan Cadence', type: 'select', options: ['Continuous', 'Hourly', 'Daily'], default: 'Hourly' },
            ]
          }
        ]
      },

      // ── Validate ────────────────────────────────────────────
      {
        id: 'validate',
        title: 'Validate',
        groups: [
          {
            title: 'Signal Requirements',
            fields: [
              { key: 'validate.minSources', label: 'Minimum Signal Sources', type: 'number', default: 2, min: 1, max: 10 },
              { key: 'validate.requiredClasses', label: 'Required Signal Classes', type: 'tags', options: ['Search', 'Social', 'Commerce'], default: ['Search', 'Social'] },
              { key: 'validate.singleSource', label: 'Allow Single-Source Validation', type: 'toggle', default: false },
            ]
          },
          {
            title: 'Temporal Thresholds',
            fields: [
              { key: 'validate.persistenceWindow', label: 'Minimum Persistence Window', type: 'select', options: ['24 hours', '3 days', '7 days', '14 days', '30 days'], default: '7 days' },
              { key: 'validate.volatilityTolerance', label: 'Volatility Tolerance', type: 'range', default: 5, min: 1, max: 10, step: 1 },
              { key: 'validate.seasonality', label: 'Seasonality Normalization', type: 'toggle', default: true },
            ]
          },
          {
            title: 'Semantic Stability',
            fields: [
              { key: 'validate.meaningDrift', label: 'Allowed Meaning Drift', type: 'range', default: 3, min: 1, max: 10, step: 1 },
              { key: 'validate.bifurcation', label: 'Bifurcation Tolerance', type: 'range', default: 5, min: 1, max: 10, step: 1 },
            ]
          },
          {
            title: 'Geographic Coherence',
            fields: [
              { key: 'validate.geoRules', label: 'Validation Scope', type: 'select', options: ['Local only', 'Regional', 'Global', 'Adaptive'], default: 'Adaptive' },
              { key: 'validate.diffusionLag', label: 'Diffusion Lag Allowance', type: 'select', options: ['None', '24 hours', '3 days', '7 days'], default: '3 days' },
            ]
          },
          {
            title: 'False Positive Sensitivity',
            fields: [
              { key: 'validate.fpSensitivity', label: 'Filtering Level', type: 'radio', options: ['Aggressive', 'Balanced', 'Permissive'], default: 'Balanced' },
            ]
          },
          {
            title: 'Ethical Gate',
            fields: [
              { key: 'validate.observeOnly', label: 'Observe-Only Domains', type: 'text', default: '', hint: 'Comma-separated domains restricted to observation' },
              { key: 'validate.blocked', label: 'Blocked Domains', type: 'text', default: '', hint: 'Comma-separated domains excluded from activation' },
            ]
          }
        ]
      },

      // ── Create ──────────────────────────────────────────────
      {
        id: 'create',
        title: 'Create',
        groups: [
          {
            title: 'Candidate Limits',
            fields: [
              { key: 'create.maxCandidates', label: 'Max Active Design Candidates', type: 'number', default: 12, min: 1, max: 50 },
              { key: 'create.autoArchive', label: 'Auto-Archive Weak Candidates', type: 'toggle', default: true },
            ]
          },
          {
            title: 'Constraint Enforcement',
            fields: [
              { key: 'create.mandatoryConstraints', label: 'Mandatory Constraints Required', type: 'number', default: 3, min: 0, max: 20 },
              { key: 'create.overridePermission', label: 'Constraint Override Permission', type: 'select', options: ['Admin only', 'Steward+', 'Creator+', 'Anyone'], default: 'Admin only' },
            ]
          },
          {
            title: 'Participation Rules',
            fields: [
              { key: 'create.participationModels', label: 'Allowed Participation Models', type: 'tags', options: ['Observe', 'Contribute', 'Co-create'], default: ['Observe', 'Contribute'] },
              { key: 'create.identityDepth', label: 'Identity Depth Required', type: 'radio', options: ['Low', 'Medium', 'High'], default: 'Medium' },
            ]
          },
          {
            title: 'Differentiation',
            fields: [
              { key: 'create.samenessTolerance', label: 'Sameness Tolerance', type: 'range', default: 3, min: 1, max: 10, step: 1 },
              { key: 'create.saturationAvoidance', label: 'Saturation Avoidance Strictness', type: 'range', default: 6, min: 1, max: 10, step: 1 },
            ]
          },
          {
            title: 'Failure Simulation',
            fields: [
              { key: 'create.mandatoryFailureTests', label: 'Mandatory Failure Tests Before Readiness', type: 'toggle', default: true },
              { key: 'create.failureModes', label: 'Required Failure Modes', type: 'tags', options: ['Market rejection', 'Timing failure', 'Audience mismatch', 'Channel saturation', 'Cultural misread'], default: ['Market rejection', 'Timing failure'] },
            ]
          },
          {
            title: 'Promotion Rules',
            fields: [
              { key: 'create.promotionRole', label: 'Who Can Declare Ready for Activation', type: 'select', options: ['Admin only', 'Steward+', 'Creator+', 'Strategist+'], default: 'Creator+' },
              { key: 'create.approvalRequired', label: 'Approval Required', type: 'toggle', default: false },
            ]
          }
        ]
      },

      // ── Activate ────────────────────────────────────────────
      {
        id: 'activate',
        title: 'Activate',
        groups: [
          {
            title: 'Activation Limits',
            fields: [
              { key: 'activate.maxConcurrent', label: 'Max Concurrent Activations', type: 'number', default: 5, min: 1, max: 50 },
              { key: 'activate.maxDuration', label: 'Max Duration per Activation', type: 'select', options: ['7 days', '14 days', '30 days', '60 days', '90 days', 'No limit'], default: '30 days' },
              { key: 'activate.allowedScale', label: 'Allowed Scale', type: 'tags', options: ['Pilot', 'Regional', 'National'], default: ['Pilot', 'Regional'] },
            ]
          },
          {
            title: 'Learning Objective Requirements',
            fields: [
              { key: 'activate.mandatoryObjective', label: 'Mandatory Learning Objective Declaration', type: 'toggle', default: true },
              { key: 'activate.objectiveTypes', label: 'Allowed Objective Types', type: 'tags', options: ['Behavioral', 'Perceptual', 'Competitive', 'Temporal', 'Economic'], default: ['Behavioral', 'Perceptual'] },
            ]
          },
          {
            title: 'Behavior Capture',
            fields: [
              { key: 'activate.participationTracking', label: 'Participation Depth Tracking', type: 'toggle', default: true },
              { key: 'activate.languageTracking', label: 'Language Shift Tracking', type: 'toggle', default: true },
              { key: 'activate.contextComparison', label: 'Context Comparison Enabled', type: 'toggle', default: false },
            ]
          },
          {
            title: 'Adaptation Rules',
            fields: [
              { key: 'activate.midChanges', label: 'Mid-Activation Changes Allowed', type: 'toggle', default: false },
              { key: 'activate.changeApproval', label: 'Change Approval Required', type: 'select', options: ['No approval', 'Admin only', 'Steward+', 'Creator+'], default: 'Admin only' },
            ]
          },
          {
            title: 'Safety Controls',
            fields: [
              { key: 'activate.autoTimeout', label: 'Automatic Activation Timeout', type: 'select', options: ['None', '7 days', '14 days', '30 days', '60 days'], default: '30 days' },
              { key: 'activate.restrictedObserve', label: 'Restricted Domains Enforce Observe-Only', type: 'toggle', default: true },
            ]
          }
        ]
      },

      // ── Learn ───────────────────────────────────────────────
      {
        id: 'learn',
        title: 'Learn',
        groups: [
          {
            title: 'Learning Aggregation',
            fields: [
              { key: 'learn.minActivations', label: 'Minimum Activations to Synthesize', type: 'number', default: 3, min: 1, max: 20 },
              { key: 'learn.weighting', label: 'Weighting Strategy', type: 'radio', options: ['Recency', 'Durability', 'Balanced'], default: 'Balanced' },
              { key: 'learn.crossContext', label: 'Cross-Context Generalization Strictness', type: 'range', default: 5, min: 1, max: 10, step: 1 },
            ]
          },
          {
            title: 'Identity Signals',
            fields: [
              { key: 'learn.adoptionThreshold', label: 'Identity Adoption Threshold', type: 'range', default: 7, min: 1, max: 10, step: 1 },
              { key: 'learn.persistenceRequirement', label: 'Persistence Requirement', type: 'select', options: ['30 days', '60 days', '90 days', '6 months'], default: '60 days' },
            ]
          },
          {
            title: 'Behavior Persistence',
            fields: [
              { key: 'learn.halfLife', label: 'Half-Life Modeling', type: 'toggle', default: true },
              { key: 'learn.decayCurve', label: 'Decay Curve Strictness', type: 'range', default: 5, min: 1, max: 10, step: 1 },
            ]
          },
          {
            title: 'Learning Promotion',
            fields: [
              { key: 'learn.promotionRole', label: 'Who Can Promote Learn → Evolve', type: 'select', options: ['Admin only', 'Steward+', 'Strategist+'], default: 'Steward+' },
              { key: 'learn.requiredConfidence', label: 'Required Confidence Level', type: 'range', default: 7, min: 1, max: 10, step: 1 },
            ]
          }
        ]
      },

      // ── Evolve ──────────────────────────────────────────────
      {
        id: 'evolve',
        title: 'Evolve',
        groups: [
          {
            title: 'Strategic Memory Rules',
            fields: [
              { key: 'evolve.createSMU', label: 'Who Can Create SMUs', type: 'select', options: ['Admin only', 'Steward+', 'Strategist+', 'Anyone'], default: 'Steward+' },
              { key: 'evolve.editSMU', label: 'Who Can Edit / Retire SMUs', type: 'select', options: ['Admin only', 'Steward+'], default: 'Admin only' },
              { key: 'evolve.memoryScope', label: 'Memory Scope', type: 'tags', options: ['Global', 'Category', 'Market'], default: ['Global', 'Category'] },
            ]
          },
          {
            title: 'Memory Decay',
            fields: [
              { key: 'evolve.decayPolicy', label: 'Decay Policy', type: 'radio', options: ['Never decay', 'Review after interval', 'Auto-flag when contradicted'], default: 'Review after interval' },
              { key: 'evolve.reviewInterval', label: 'Review Interval', type: 'select', options: ['30 days', '60 days', '90 days', '6 months', '1 year'], default: '90 days' },
            ]
          },
          {
            title: 'System Feedback',
            description: 'Which modes do Strategic Memory Units influence',
            fields: [
              { key: 'evolve.influenceSense', label: 'Sense Weighting', type: 'toggle', default: true },
              { key: 'evolve.influenceValidate', label: 'Validate Thresholds', type: 'toggle', default: true },
              { key: 'evolve.influenceCreate', label: 'Create Defaults', type: 'toggle', default: true },
              { key: 'evolve.influenceActivate', label: 'Activate Warnings', type: 'toggle', default: false },
            ]
          },
          {
            title: 'Repeat-Mistake Handling',
            fields: [
              { key: 'evolve.repeatHandling', label: 'Handling Strategy', type: 'radio', options: ['Warn only', 'Warn + require acknowledgment', 'Block (ethics only)'], default: 'Warn + require acknowledgment' },
            ]
          }
        ]
      }
    ]
  },

  // ── 5. Memory & Governance ────────────────────────────────────
  {
    id: 'governance',
    title: 'Memory & Governance',
    subtitle: 'Audit, trust, and decision traceability',
    groups: [
      {
        title: 'Audit & Logging',
        fields: [
          { key: 'gov.auditLogging', label: 'Audit Logging', type: 'toggle', default: true },
          { key: 'gov.decisionTrace', label: 'Decision Trace Visibility', type: 'select', options: ['Admin only', 'Steward+', 'All roles'], default: 'Steward+' },
          { key: 'gov.provenance', label: 'Learning Provenance Required', type: 'select', options: ['None', 'Source only', 'Full chain'], default: 'Source only' },
        ]
      },
      {
        title: 'Export & Permissions',
        fields: [
          { key: 'gov.exportPermission', label: 'Export Permissions', type: 'select', options: ['Admin only', 'Steward+', 'All roles'], default: 'Admin only' },
          { key: 'gov.exportFormat', label: 'Default Export Format', type: 'select', options: ['JSON', 'CSV', 'Both'], default: 'JSON' },
        ]
      },
      {
        title: 'Notifications & Alerts',
        description: 'System alerts for operational awareness',
        fields: [
          { key: 'gov.alertStalled', label: 'Stalled Activation Alerts', type: 'toggle', default: true },
          { key: 'gov.alertContradicted', label: 'Contradicted SMU Alerts', type: 'toggle', default: true },
          { key: 'gov.alertValidation', label: 'Validation Failure Alerts', type: 'toggle', default: false },
          { key: 'gov.digestFrequency', label: 'Digest Frequency', type: 'select', options: ['Off', 'Daily', 'Weekly'], default: 'Weekly' },
        ]
      }
    ]
  },

  // ── 6. System Health ──────────────────────────────────────────
  {
    id: 'health',
    title: 'System Health',
    subtitle: 'Operational status and diagnostics (read-only)',
    groups: [
      {
        title: 'Data Pipeline',
        fields: [
          { key: 'health.freshness', label: 'Dataset Freshness', type: 'readout', default: '2 hours ago' },
          { key: 'health.latency', label: 'Signal Latency', type: 'readout', default: '340ms avg' },
          { key: 'health.pipelineStatus', label: 'Pipeline Status', type: 'readout', default: 'Healthy' },
        ]
      },
      {
        title: 'Active Objects',
        fields: [
          { key: 'health.activeActivations', label: 'Active Activations', type: 'readout', default: '3' },
          { key: 'health.stalledActivations', label: 'Stalled Activations', type: 'readout', default: '0' },
          { key: 'health.orphanedCandidates', label: 'Orphaned Candidates', type: 'readout', default: '0' },
          { key: 'health.activeCandidates', label: 'Active Candidates', type: 'readout', default: '8' },
        ]
      },
      {
        title: 'Strategic Memory',
        fields: [
          { key: 'health.totalSMUs', label: 'Total SMUs', type: 'readout', default: '24' },
          { key: 'health.growthRate', label: 'Memory Growth Rate', type: 'readout', default: '+2.1 SMUs / month' },
          { key: 'health.contradictions', label: 'Active Contradictions', type: 'readout', default: '1' },
          { key: 'health.pendingReviews', label: 'Pending Reviews', type: 'readout', default: '3' },
        ]
      }
    ]
  }
];


// ─── Settings Interaction Class ─────────────────────────────────

export class SettingsInteraction {
  private overlay: HTMLDivElement;
  private nav!: HTMLDivElement;
  private content!: HTMLDivElement;
  private activeSection: string = 'account';
  private activeModeTab: string = 'sense';
  private values: Record<string, any> = {};
  private isOpen: boolean = false;
  private escHandler: (e: KeyboardEvent) => void;

  constructor() {
    this.loadValues();
    this.overlay = document.createElement('div');
    this.overlay.id = 'settings-panel';
    this.overlay.innerHTML = this.buildShell();
    document.body.appendChild(this.overlay);

    this.nav = this.overlay.querySelector('.settings-nav')!;
    this.content = this.overlay.querySelector('.settings-content')!;

    this.bindNav();
    this.bindSettingsButton();
    this.bindClose();
    this.renderSection(this.activeSection);

    // ESC to close
    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this.escHandler);
  }

  // ── Shell HTML ──────────────────────────────────────────────

  private buildShell(): string {
    return `
      <div class="settings-backdrop"></div>
      <div class="settings-container">
        <div class="settings-sidebar">
          <div class="settings-sidebar-header">
            <span class="settings-sidebar-title">Control Panel</span>
            <button class="settings-close">&times;</button>
          </div>
          <nav class="settings-nav">
            ${SECTIONS.map(s => `
              <div class="settings-nav-item ${s.id === this.activeSection ? 'active' : ''}" data-section="${s.id}">
                ${s.title}
              </div>
            `).join('')}
          </nav>
          <div class="settings-sidebar-footer">
            <span class="settings-version">AURA v0.1</span>
          </div>
        </div>
        <div class="settings-main">
          <div class="settings-content"></div>
        </div>
      </div>
    `;
  }

  // ── Navigation ──────────────────────────────────────────────

  private bindNav(): void {
    this.nav.addEventListener('click', (e) => {
      const item = (e.target as HTMLElement).closest('.settings-nav-item') as HTMLElement;
      if (!item) return;
      const sectionId = item.dataset.section!;
      this.activeSection = sectionId;

      // Update active state
      this.nav.querySelectorAll('.settings-nav-item').forEach(el =>
        el.classList.toggle('active', (el as HTMLElement).dataset.section === sectionId)
      );

      this.renderSection(sectionId);
    });
  }

  private bindSettingsButton(): void {
    const btn = document.querySelector('.mode-settings-btn');
    btn?.addEventListener('click', () => this.open());
  }

  private bindClose(): void {
    const closeBtn = this.overlay.querySelector('.settings-close');
    closeBtn?.addEventListener('click', () => this.close());
    const backdrop = this.overlay.querySelector('.settings-backdrop');
    backdrop?.addEventListener('click', () => this.close());
  }

  // ── Open / Close ────────────────────────────────────────────

  open(): void {
    this.overlay.classList.add('visible');
    this.isOpen = true;
  }

  close(): void {
    this.overlay.classList.remove('visible');
    this.isOpen = false;
  }

  // ── Section Rendering ───────────────────────────────────────

  private renderSection(sectionId: string): void {
    const section = SECTIONS.find(s => s.id === sectionId);
    if (!section) return;

    let html = `
      <div class="settings-section-header">
        <h2 class="settings-section-title">${section.title}</h2>
        <p class="settings-section-subtitle">${section.subtitle}</p>
      </div>
    `;

    // Mode Controls: render mode tabs
    if (section.modeTabs) {
      html += `
        <div class="settings-mode-tabs">
          ${section.modeTabs.map(tab => `
            <span class="settings-mode-tab ${tab.id === this.activeModeTab ? 'active' : ''}" data-mode-tab="${tab.id}">${tab.title}</span>
          `).join('')}
        </div>
      `;

      const activeTab = section.modeTabs.find(t => t.id === this.activeModeTab) || section.modeTabs[0];
      html += this.renderGroups(activeTab.groups);
    } else if (section.groups) {
      html += this.renderGroups(section.groups);
    }

    this.content.innerHTML = html;

    // Bind mode tab clicks
    if (section.modeTabs) {
      this.content.querySelectorAll('.settings-mode-tab').forEach(tab => {
        tab.addEventListener('click', () => {
          this.activeModeTab = (tab as HTMLElement).dataset.modeTab!;
          this.renderSection(sectionId);
        });
      });
    }

    // Bind all interactive controls
    this.bindControls();
  }

  private renderGroups(groups: Group[]): string {
    return groups.map(group => `
      <div class="settings-group">
        <div class="settings-group-header">
          <h3 class="settings-group-title">${group.title}</h3>
          ${group.description ? `<p class="settings-group-desc">${group.description}</p>` : ''}
        </div>
        ${group.fields.map(f => this.renderField(f)).join('')}
      </div>
    `).join('');
  }

  // ── Field Rendering ─────────────────────────────────────────

  private renderField(field: Field): string {
    const value = this.values[field.key] ?? field.default;

    switch (field.type) {
      case 'toggle':
        return `
          <div class="settings-field">
            <label class="settings-field-label">${field.label}</label>
            <label class="settings-toggle">
              <input type="checkbox" data-key="${field.key}" ${value ? 'checked' : ''} />
              <span class="settings-toggle-track"><span class="settings-toggle-thumb"></span></span>
            </label>
          </div>
        `;

      case 'select':
        return `
          <div class="settings-field">
            <label class="settings-field-label">${field.label}</label>
            <select class="settings-select" data-key="${field.key}">
              ${(field.options || []).map(opt =>
                `<option value="${opt}" ${opt === value ? 'selected' : ''}>${opt}</option>`
              ).join('')}
            </select>
          </div>
        `;

      case 'number':
        return `
          <div class="settings-field">
            <label class="settings-field-label">${field.label}</label>
            <div class="settings-number-wrap">
              <input type="number" class="settings-number" data-key="${field.key}" value="${value}"
                min="${field.min ?? ''}" max="${field.max ?? ''}" step="${field.step ?? 1}" />
              ${field.unit ? `<span class="settings-unit">${field.unit}</span>` : ''}
            </div>
          </div>
        `;

      case 'text':
        return `
          <div class="settings-field">
            <label class="settings-field-label">${field.label}</label>
            <input type="text" class="settings-text" data-key="${field.key}"
              value="${value}" placeholder="${field.hint ?? ''}" />
          </div>
        `;

      case 'range':
        return `
          <div class="settings-field">
            <label class="settings-field-label">${field.label}</label>
            <div class="settings-range-wrap">
              <input type="range" class="settings-range" data-key="${field.key}"
                value="${value}" min="${field.min ?? 0}" max="${field.max ?? 10}" step="${field.step ?? 1}" />
              <span class="settings-range-value">${value} / ${field.max ?? 10}</span>
            </div>
          </div>
        `;

      case 'radio':
        return `
          <div class="settings-field settings-field-stacked">
            <label class="settings-field-label">${field.label}</label>
            <div class="settings-radio-group">
              ${(field.options || []).map(opt => `
                <label class="settings-radio-pill ${opt === value ? 'active' : ''}">
                  <input type="radio" name="${field.key}" value="${opt}" data-key="${field.key}" ${opt === value ? 'checked' : ''} />
                  ${opt}
                </label>
              `).join('')}
            </div>
          </div>
        `;

      case 'tags':
        const activeTags: string[] = Array.isArray(value) ? value : [value];
        return `
          <div class="settings-field settings-field-stacked">
            <label class="settings-field-label">${field.label}</label>
            <div class="settings-tags" data-key="${field.key}">
              ${(field.options || []).map(opt => `
                <span class="settings-tag ${activeTags.includes(opt) ? 'active' : ''}" data-value="${opt}">${opt}</span>
              `).join('')}
            </div>
          </div>
        `;

      case 'readout':
        return `
          <div class="settings-field">
            <label class="settings-field-label">${field.label}</label>
            <span class="settings-readout">${value}</span>
          </div>
        `;

      default:
        return '';
    }
  }

  // ── Control Binding ─────────────────────────────────────────

  private bindControls(): void {
    // Toggles
    this.content.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(el => {
      el.addEventListener('change', () => this.saveValue(el.dataset.key!, el.checked));
    });

    // Selects
    this.content.querySelectorAll<HTMLSelectElement>('.settings-select').forEach(el => {
      el.addEventListener('change', () => this.saveValue(el.dataset.key!, el.value));
    });

    // Number inputs
    this.content.querySelectorAll<HTMLInputElement>('.settings-number').forEach(el => {
      el.addEventListener('change', () => this.saveValue(el.dataset.key!, Number(el.value)));
    });

    // Text inputs
    this.content.querySelectorAll<HTMLInputElement>('.settings-text').forEach(el => {
      el.addEventListener('change', () => this.saveValue(el.dataset.key!, el.value));
    });

    // Range sliders
    this.content.querySelectorAll<HTMLInputElement>('.settings-range').forEach(el => {
      el.addEventListener('input', () => {
        const display = el.parentElement?.querySelector('.settings-range-value');
        if (display) display.textContent = `${el.value} / ${el.max}`;
        this.saveValue(el.dataset.key!, Number(el.value));
      });
    });

    // Radio pills
    this.content.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(el => {
      el.addEventListener('change', () => {
        // Update active state on pills
        const group = el.closest('.settings-radio-group');
        group?.querySelectorAll('.settings-radio-pill').forEach(pill =>
          pill.classList.toggle('active', (pill.querySelector('input') as HTMLInputElement)?.checked)
        );
        this.saveValue(el.dataset.key!, el.value);
      });
    });

    // Tags (toggle on click)
    this.content.querySelectorAll('.settings-tags').forEach(container => {
      const key = (container as HTMLElement).dataset.key!;
      container.querySelectorAll('.settings-tag').forEach(tag => {
        tag.addEventListener('click', () => {
          tag.classList.toggle('active');
          const activeTags = Array.from(container.querySelectorAll('.settings-tag.active'))
            .map(t => (t as HTMLElement).dataset.value!);
          this.saveValue(key, activeTags);
        });
      });
    });
  }

  // ── Persistence (hybrid localStorage + Supabase) ───────────

  private loadValues(): void {
    // Synchronous pass: pull any cached per-key values from localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const lsKey = localStorage.key(i);
      if (lsKey?.startsWith('aura-setting-')) {
        try {
          const cleanKey = lsKey.replace('aura-setting-', '');
          this.values[cleanKey] = JSON.parse(localStorage.getItem(lsKey)!);
        } catch {}
      }
    }

    // Also load old blob format for backward compat
    try {
      const stored = localStorage.getItem('aura_settings');
      if (stored) {
        const blob = JSON.parse(stored);
        for (const [k, v] of Object.entries(blob)) {
          if (!(k in this.values)) this.values[k] = v;
        }
        // Migrate: write each key to per-key format, then remove blob
        for (const [k, v] of Object.entries(blob)) {
          localStorage.setItem(`aura-setting-${k}`, JSON.stringify(v));
        }
        localStorage.removeItem('aura_settings');
      }
    } catch {}

    // Async pass: overlay with Supabase data (source of truth)
    this.loadRemoteSettings();
  }

  private async loadRemoteSettings(): Promise<void> {
    try {
      const remote = await loadAllSettings();
      let needsRerender = false;
      for (const [key, value] of Object.entries(remote)) {
        if (JSON.stringify(this.values[key]) !== JSON.stringify(value)) {
          this.values[key] = value;
          needsRerender = true;
        }
      }
      if (needsRerender) {
        this.renderSection(this.activeSection);
      }
    } catch { /* remote unavailable, local values stand */ }

    // Subscribe to realtime changes from other devices
    this.unsubscribeRealtime = subscribeToChanges((key, value) => {
      this.values[key] = value;
      this.renderSection(this.activeSection);
    });
  }

  private unsubscribeRealtime: (() => void) | null = null;

  private saveValue(key: string, value: any): void {
    this.values[key] = value;
    // Hybrid write: localStorage (instant) + Supabase (async)
    saveSetting(key, value);
  }

  // ── Cleanup ─────────────────────────────────────────────────

  dispose(): void {
    document.removeEventListener('keydown', this.escHandler);
    this.unsubscribeRealtime?.();
    this.overlay.remove();
  }
}
